// Setter tracking contract (SPEC §19, 2026-10-01; TRACKING-SPEC-2026-10-01.md §4.5). Owner: tracking agent.
// TRACKING  POST /api/tracking/presence PresenceBeat -> 204
//           GET  /api/tracking/scoreboard?from=&to=&userId=&campaignId= -> Scoreboard   (setter: own row only)
//           GET  /api/tracking/live -> LiveRow[]                                          (admin)
//           GET  /api/tracking/timeline?userId=&day=YYYY-MM-DD -> TimelineEvent[]         (setter: own only)
//           GET  /api/tracking/calls.csv?from=&to=&userId=&campaignId= -> text/csv        (admin)
//           GET  /api/tracking/targets -> TargetsSetting (admin) | {effective: SetterTargets} (setter)
//           PUT  /api/tracking/targets TargetsSetting -> TargetsSetting                   (admin)
// Not measured is null, never 0. No response here ever carries call-recording data (enforced by tests/trk-routes).
import type { DispositionCode, ISODate } from './api';

export type PresenceState = 'in_call' | 'wrap' | 'ready' | 'browsing' | 'paused';
export const PRESENCE_STATES: readonly PresenceState[] = ['in_call', 'wrap', 'ready', 'browsing', 'paused'];
export interface PresenceBeat { state: PresenceState; campaignId?: number | null }

export interface SetterTargets {
  dialsPerDay: number | null; dmConversationsPerDay: number | null; conversations3mPerDay: number | null;
  meetingsPerWeek: number | null; talkMinutesPerDay: number | null; practiceAvgMin: number | null;
  dialsPerMeetingMax: number | null; showRateMin: number | null;
}
export const TARGET_KEYS: ReadonlyArray<keyof SetterTargets> = [
  'dialsPerDay', 'dmConversationsPerDay', 'conversations3mPerDay', 'meetingsPerWeek', 'talkMinutesPerDay', 'practiceAvgMin',
  'dialsPerMeetingMax', 'showRateMin',
];
export interface TargetsSetting { default: SetterTargets; users: Record<string, Partial<SetterTargets>> }

export interface GapStats { over10: number; over20: number; longestMin: number | null; totalOver5Min: number }
export interface ScoreDay {
  ymd: string; dials: number; connects: number; conv3m: number; meetingsBooked: number;
  firstDialAt: ISODate | null; lastDialAt: ISODate | null; onDialerMinutes: number | null; gaps: GapStats;
}

export interface ScoreMetrics {
  activeDays: number; dials: number; answered: number; answerRate: number | null; connects: number; connectRate: number | null;
  dmConversations: number; conv60: number; conv3m: number; meetingsBooked: number; setRate: number | null;
  dialsPerMeeting: number | null; meetingsHeld: number; meetingsNoShow: number; meetingsUnlogged: number;
  showRate: number | null; talkMinutes: number; lineMinutes: number; avgWrapSec: number | null;
  onDialerMinutes: number | null; dialsPerHour: number | null; leadsServed: number | null; servedNotDialled: number | null;
  vmLive: number; dncAdds: number; gateBlocks: number; undispositioned: number; noAnswerSuspects: number;
  callbacksOverdue: number; gaps: GapStats;
  firstDialAt: ISODate | null; lastDialAt: ISODate | null;
}
export interface ScoreRow extends ScoreMetrics {
  userId: number; name: string; role: 'admin' | 'setter';
  targets: SetterTargets;                         // effective (default merged with per-user)
  practice: { avgScore30d: number | null; passed7d: number; lastPracticeAt: ISODate | null } | null;
  dispositions: Partial<Record<DispositionCode, number>>;
  days: ScoreDay[];                               // one per Toronto day in range (range <= 31 days), else []
}
export interface Scoreboard { fromYmd: string; toYmd: string; tz: string; generatedAt: ISODate; rows: ScoreRow[]; team: ScoreMetrics }

export interface LiveRow {
  userId: number; name: string; state: PresenceState | 'offline'; stateSince: ISODate | null;  // offline = no beat in 2 min
  campaignId: number | null; campaignName: string | null; liveCallId: number | null;
  lastDialAt: ISODate | null; minutesSinceLastDial: number | null;
  dialsToday: number; connectsToday: number; conv3mToday: number; meetingsToday: number; dialTarget: number | null;
  idle: boolean;                                  // on the dialer (not offline, no live call) and last dial >= idleMinutes ago
}
export interface LiveBoard { generatedAt: ISODate; idleMinutes: number; rows: LiveRow[] }

export type TimelineEvent =
  | { at: ISODate; kind: 'login' }
  | { at: ISODate; kind: 'presence'; state: PresenceState; minutes: number }                 // contiguous runs
  | { at: ISODate; kind: 'served' | 'opened'; membershipId: number; businessName: string | null }
  | { at: ISODate; kind: 'blocked'; reasons: string[] }
  | { at: ISODate; kind: 'call'; callId: number; businessName: string | null; stateProv: string | null; answered: boolean;
      durationSec: number | null; disposition: DispositionCode | null; wrapSec: number | null }
  | { at: ISODate; kind: 'meeting'; meetingId: number; businessName: string | null; scheduledFor: ISODate }
  | { at: ISODate; kind: 'dnc'; businessName: string | null }
  | { at: ISODate; kind: 'gap'; minutes: number };                                         // >= 5 min between calls
